import { useCallback } from 'react';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useNoticeBannersStackContext } from '@sweetgreen/garnish';

import { useSubscribeMutation } from '@order/graphql';
import { useSweetpassTier } from '@order/hooks';
import { useLocalizationContext } from '@order/Localization';
import { useTelemetry } from '@order/Telemetry';
import { getUrqlError } from '@order/utils';

import type { AppStackParamList } from '../../../../navigation/AppNavigation.props';

/**
 * Flow for upgrading to sweetpass plus.
 * We call the subscribe mutation with planId and paymentId.
 * If it fails, we track the failure and show an error banner.
 * If it succeeds, we track the success and refetch the sweetpass tier.
 * If we started the flow from the bag, we return to the bag.
 * Otherwise we show the celebration modal.
 */
export const useSubscribeToSweetpassPlus = (
  props: SubscribeToSweetpassPlusProps,
) => {
  const {
    planId,
    paymentId,
    redirectToAfterUpgrade,
    setHasCustomerBeenUpgraded,
  } = props;

  // ─── Context ─────────────────────────────────────────────────────────

  const replaceWithBag = useReplaceWithBag();
  const { t } = useLocalizationContext();
  const { track } = useTelemetry();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const shouldRedirectToBagAfterUpgrade = redirectToAfterUpgrade === 'bag';

  // ─── Queries ─────────────────────────────────────────────────────────

  const { refetchSweetpassTier } = useSweetpassTier();
  const [, subscribeMutation] = useSubscribeMutation();

  return useCallback(async () => {
    const response = await subscribeMutation({ planId, paymentId });
    const result = response?.data?.createSubscription?.__typename;

    if (result !== 'CreateSubscriptionResult') {
      addNoticeBanner({
        id: 'sweetpass-subscription-error',
        text: t('sweetpass.checkout.join-sweetpass-plus.notice.failure'),
        palette: 'caution',
      });
      track('sweetpass_account.upgrade.failure', {
        systemError: getUrqlError(response?.error),
      });

      return;
    }

    track('sweetpass_account.upgrade.success');
    refetchSweetpassTier({ requestPolicy: 'cache-and-network' });

    if (shouldRedirectToBagAfterUpgrade) {
      replaceWithBag();

      return;
    }

    setHasCustomerBeenUpgraded(true);
  }, [
    planId,
    paymentId,
    shouldRedirectToBagAfterUpgrade,
    t,
    track,
    refetchSweetpassTier,
    subscribeMutation,
    setHasCustomerBeenUpgraded,
    addNoticeBanner,
    replaceWithBag,
  ]);
};

// ─── Hooks ─────────────────────────────────────────────────────────────

/**
 * If the upgrade flow was triggered from the bag, we return to it by skipping
 * the upgrade modal, and we use `replace` here because we don't  want to keep
 * the current screen in history since it should be unavailable post upgrade.
 */
const useReplaceWithBag = () => {
  const navigation =
    useNavigation<NativeStackNavigationProp<AppStackParamList>>();

  return useCallback(() => {
    navigation.replace('Modal', {
      screen: 'Bag',
      params: { referrer: 'sweetpass-upgrade' },
    });
  }, [navigation]);
};

// ─── Types ─────────────────────────────────────────────────────────────

type SubscribeToSweetpassPlusProps = {
  readonly planId: string;
  readonly paymentId?: string;
  readonly redirectToAfterUpgrade?: 'bag' | 'order-status';
  readonly setHasCustomerBeenUpgraded: (toggle: boolean) => void;
};
